import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Warehouse, ShieldCheck, Wrench, Star, ChevronRight, Bike, Tag, Award, Settings, Truck, BadgeCheck, CreditCard, Sparkles, Repeat, PackageCheck, MapPin } from "lucide-react";
import { apiClient } from "../lib/api";
import MotorcycleCard from "../components/MotorcycleCard";
import CarCard from "../components/CarCard";
import Seo from "../components/Seo";

const HERO_BG = "https://images.pexels.com/photos/31847440/pexels-photo-31847440.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";
const ABOUT_IMG = "https://images.pexels.com/photos/30396826/pexels-photo-30396826.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

const ICON_MAP = {
  warehouse: Warehouse, shield: ShieldCheck, wrench: Wrench, bike: Bike,
  tag: Tag, award: Award, settings: Settings, truck: Truck,
  badge: BadgeCheck, "credit-card": CreditCard, sparkles: Sparkles,
};

const DEFAULT_FEATURES = [
  { icon: "badge", title: "Kurumsal Güven", description: "Gensan Motors, kayıtlı ve kurumsal yapısıyla faaliyet gösteren güvenilir bir markadır. Faturalı satış, resmi sözleşme ve kurumsal garanti ile her adımda arkanızdayız." },
  { icon: "shield", title: "Şeffaf & Garantili Alışveriş", description: "Her motosiklet uzman ekibimizce noktasına kadar ekspertiz edilir. Hasar kaydı, kilometre ve teknik durum açıkça paylaşılır; pişman olmayacağınız bir alışveriş garanti edilir." },
  { icon: "wrench", title: "Satış Sonrası Tam Destek", description: "İş bittiğinde yalnız bırakmıyoruz. Yetkili servis, yedek parça, sigorta ve finansman çözümleriyle motorunuzun ömrü boyunca profesyonel destek sunuyoruz." },
];

const OTHER_SERVICES = [
  { Icon: Repeat, title: "Konsinye Motor Satışı", description: "Motorunuzu güvenle bize emanet edin; profesyonel ekibimiz sizin adınıza en iyi fiyata satışını gerçekleştirsin." },
  { Icon: ShieldCheck, title: "Sigorta Hizmeti", description: "Trafik ve kasko sigortası işlemlerinizi tek noktadan, hızlı ve avantajlı şekilde tamamlıyoruz." },
  { Icon: Truck, title: "Çekici Hizmeti", description: "Yolda kaldığınızda 7/24 çekici desteğiyle motorunuzu güvenle bulunduğunuz yerden alıyoruz." },
  { Icon: PackageCheck, title: "Nakliye Hizmeti", description: "Türkiye'nin her yerine sigortalı ve hasarsız motosiklet nakliye çözümleri sunuyoruz." },
  { Icon: MapPin, title: "Adrese Teslim Çözümler", description: "Satın aldığınız motoru istediğiniz adrese, kapınıza kadar teslim ediyoruz." },
  { Icon: CreditCard, title: "Finansal Hizmetler", description: "Anlaşmalı bankalarla kredi ve taksit imkanları ile hayalinizdeki motora kolayca sahip olun." },
];

const BRANDS = [
  { name: "ABUS", logo: "/bayilik/abus.png" },
  { name: "Musatti Motorcycle", logo: "/bayilik/musatti.png" },
  { name: "Karoval Motoworks", logo: "/bayilik/karoval.png" },
  { name: "Alba", logo: "/bayilik/alba.png" },
  { name: "Corelli", logo: "/bayilik/corelli.png" },
  { name: "SFM Motor", logo: "/bayilik/sfm.png" },
  { name: "Yadea", logo: "/bayilik/yadea.png" },
];

const Home = () => {
  const [featured, setFeatured] = useState([]);
  const [featuredCars, setFeaturedCars] = useState([]);
  const [stats, setStats] = useState({ happy_customers: 500, motorcycles_in_stock: 120, years_experience: 15, brands_count: 30 });
  const [reviews, setReviews] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiClient.get("/motorcycles", { params: { featured: true, limit: 4 } }).then(r => r.data).catch(() => []),
      apiClient.get("/stats").then(r => r.data).catch(() => null),
      apiClient.get("/reviews").then(r => r.data).catch(() => []),
      apiClient.get("/services").then(r => r.data).catch(() => []),
      apiClient.get("/cars", { params: { featured: true, limit: 4 } }).then(r => r.data).catch(() => []),
    ]).then(([m, s, r, sv, c]) => {
      setFeatured(m);
      setFeaturedCars(c);
      if (s) setStats(s);
      setReviews(r.slice(0, 3));
      setServices(sv);
      setLoading(false);
    });
  }, []);

  const featuresToShow = DEFAULT_FEATURES;

  return (
    <div data-testid="home-page" className="bg-zinc-950 text-white">
      <Seo
        title="Motosiklet Satış İstanbul"
        description="Gensan Motors İstanbul Maltepe'de sıfır ve ikinci el motosiklet galerisi. Geniş stok, yetkili bayilik, servis ve bakım, finansman ve sigorta çözümleri. Hayalinizdeki motora ulaşın."
        keywords="motosiklet galerisi, motor satışı, motosiklet bayi servis"
        path="/"
      />
      {/* HERO */}
      <section data-testid="hero-section" className="relative h-screen min-h-[700px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="Gensan Motors" className="w-full h-full object-cover anim-ken-burns" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-black/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 z-10">
          <div className="max-w-3xl anim-slide-up">
            <div className="flex items-center gap-3 mb-6">
              <span className="block h-px w-12 bg-orange-500" />
              <span className="text-orange-500 font-bold uppercase tracking-[0.3em] text-xs">Gensan Motors</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-bold uppercase leading-[0.95] tracking-tight">
              HAYALLERİNİZİ<br /><span className="text-orange-500">ERTELEMEYİN</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-zinc-300 max-w-xl leading-relaxed">
              Sıfır ve ikinci el motosiklet galerisi. Geniş stok, uzman ekip ve güvenilir alışveriş deneyimiyle yanınızdayız.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/sifir-motorlar" data-testid="hero-cta-button" className="group inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-700 text-white px-8 py-5 font-bold uppercase tracking-widest text-sm transition-all">
                Stoğu İncele <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/hakkimizda" data-testid="hero-secondary-button" className="inline-flex items-center gap-3 border border-white/40 hover:border-white hover:bg-white hover:text-black text-white px-8 py-5 font-bold uppercase tracking-widest text-sm transition-all">
                Daha Fazla
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-black/80 backdrop-blur-md border-t border-zinc-900 z-10">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 divide-x divide-zinc-900">
            {[
              { value: `${stats.happy_customers}+`, label: "Mutlu Müşteri" },
              { value: `${stats.motorcycles_in_stock}+`, label: "Stoktaki Motor" },
              { value: `${stats.years_experience}+`, label: "Yıllık Tecrübe" },
              { value: `${stats.brands_count}+`, label: "Marka" },
            ].map((s) => (
              <div key={s.label} className="py-6 px-2 text-center">
                <div className="font-display text-3xl md:text-4xl font-bold text-orange-500">{s.value}</div>
                <div className="mt-1 text-[10px] md:text-xs uppercase tracking-widest text-zinc-400">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED MOTORCYCLES */}
      <section data-testid="featured-section" className="py-24 md:py-32 bg-zinc-950">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="block h-px w-12 bg-orange-500" />
                <span className="text-orange-500 font-bold uppercase tracking-[0.3em] text-xs">Vitrin</span>
              </div>
              <h2 className="font-display text-4xl md:text-6xl font-bold uppercase leading-none tracking-tight">
                Öne Çıkan <span className="text-orange-500">Motorlar</span>
              </h2>
            </div>
            <Link to="/sifir-motorlar" data-testid="view-all-link" className="inline-flex items-center gap-2 text-white hover:text-orange-500 font-bold uppercase tracking-widest text-xs transition-colors">
              Tüm Stoğu Gör <ChevronRight size={16} />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1,2,3,4].map(i => <div key={i} className="aspect-[4/3] bg-zinc-900 animate-pulse" />)}
            </div>
          ) : featured.length === 0 ? (
            <div className="border border-zinc-900 p-16 text-center">
              <p className="text-zinc-400 mb-6 uppercase tracking-widest text-sm">Henüz öne çıkan motor eklenmedi</p>
              <Link to="/admin" className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 font-bold uppercase tracking-widest text-xs">
                Admin Panele Git <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featured.map((m, i) => <MotorcycleCard key={m.id} moto={m} index={i} />)}
            </div>
          )}
        </div>
      </section>

      {/* FEATURED CARS */}
      {featuredCars.length > 0 && (
        <section data-testid="featured-cars-section" className="py-24 md:py-32 bg-black">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="block h-px w-12 bg-orange-500" />
                  <span className="text-orange-500 font-bold uppercase tracking-[0.3em] text-xs">Vitrin</span>
                </div>
                <h2 className="font-display text-4xl md:text-6xl font-bold uppercase leading-none tracking-tight">
                  Öne Çıkan <span className="text-orange-500">Araçlar</span>
                </h2>
              </div>
              <Link to="/ikinci-el-araclar" data-testid="view-all-cars-link" className="inline-flex items-center gap-2 text-white hover:text-orange-500 font-bold uppercase tracking-widest text-xs transition-colors">
                Tüm Araçları Gör <ChevronRight size={16} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredCars.map((c, i) => <CarCard key={c.id} car={c} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* WHY GENSAN / SERVICES */}
      <section data-testid="why-section" className="py-24 md:py-32 bg-black relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-orange-600/10 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="block h-px w-8 bg-orange-500" />
              <span className="text-orange-500 font-bold uppercase tracking-[0.3em] text-xs">
                Kurumsal Avantajlar
              </span>
              <span className="block h-px w-8 bg-orange-500" />
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-bold uppercase leading-none tracking-tight">
              Neden <span className="text-orange-500">Gensan Motors</span>?
            </h2>
            <p className="mt-6 max-w-2xl mx-auto text-zinc-300 text-base md:text-lg leading-relaxed">
              Gensan Motors; yılların tecrübesini kurumsal bir disiplinle birleştiren, profesyonel ekibi ve şeffaf iş anlayışıyla sektörde fark yaratan bir markadır. Kurumsal yapımız sayesinde her işlem kayıt altında, faturalı ve güvence altındadır. Sizin için bir motosiklet almak; sadece bir alışveriş değil, güvenle başlayan uzun soluklu bir yolculuktur.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-zinc-900">
            {featuresToShow.map((f, i) => {
              const Icon = ICON_MAP[f.icon] || Wrench;
              return (
                <div key={i} data-testid={`feature-${i}`} className={`p-10 md:p-12 group hover:bg-zinc-950 transition-colors ${i < featuresToShow.length - 1 ? "md:border-r border-zinc-900" : ""} ${i < featuresToShow.length - 1 ? "border-b md:border-b-0 border-zinc-900" : ""}`}>
                  <div className="w-14 h-14 bg-orange-600 flex items-center justify-center mb-8 group-hover:rotate-6 transition-transform">
                    <Icon size={26} className="text-white" />
                  </div>
                  <h3 className="font-display text-2xl font-bold uppercase mb-4 tracking-tight">{f.title}</h3>
                  <p className="text-zinc-400 leading-relaxed">{f.description}</p>
                  <div className="mt-8 text-7xl font-display font-bold text-zinc-900 group-hover:text-orange-600/20 transition-colors">0{i + 1}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* DIĞER HİZMETLER */}
      <section data-testid="other-services-section" className="py-24 md:py-32 bg-zinc-950 relative overflow-hidden">
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-orange-600/10 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-end mb-16">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="block h-px w-12 bg-orange-500" />
                <span className="text-orange-500 font-bold uppercase tracking-[0.3em] text-xs">Diğer Hizmetler</span>
              </div>
              <h2 className="font-display text-4xl md:text-6xl font-bold uppercase leading-none tracking-tight">
                Satıştan Fazlası, <span className="text-orange-500">Tam Çözüm</span>
              </h2>
            </div>
            <p className="text-zinc-300 text-lg leading-relaxed">
              Gensan Motors yalnızca bir galeri değil; motosiklet sahipliğinin her aşamasında yanınızda olan bir çözüm ortağıdır. Satıştan sigortaya, çekiciden finansmana kadar ihtiyacınız olan tüm hizmetleri tek çatı altında sunuyoruz.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border border-zinc-900">
            {OTHER_SERVICES.map((s, i) => (
              <div
                key={i}
                data-testid={`other-service-${i}`}
                className="p-10 group hover:bg-black transition-colors border-zinc-900 border-b sm:[&:nth-last-child(-n+1)]:border-b-0 lg:[&:nth-last-child(-n+3)]:border-b-0 sm:[&:nth-child(odd)]:border-r lg:[&:nth-child(odd)]:border-r-0 lg:[&:not(:nth-child(3n))]:border-r"
              >
                <div className="w-14 h-14 bg-orange-600 flex items-center justify-center mb-8 group-hover:rotate-6 transition-transform">
                  <s.Icon size={26} className="text-white" />
                </div>
                <h3 className="font-display text-2xl font-bold uppercase mb-4 tracking-tight">{s.title}</h3>
                <p className="text-zinc-400 leading-relaxed">{s.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-6 bg-black border border-zinc-900 p-8 md:p-10">
            <p className="font-display text-xl md:text-2xl font-bold uppercase tracking-tight text-center sm:text-left">
              Hizmetlerimiz hakkında bilgi almak ister misiniz?
            </p>
            <Link to="/iletisim" data-testid="other-services-cta" className="shrink-0 inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 font-bold uppercase tracking-widest text-sm transition-colors">
              Bize Ulaşın <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* BAYİLİKLERİMİZ */}
      <section data-testid="brands-section" className="py-24 md:py-32 bg-black relative overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-orange-600/10 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="block h-px w-8 bg-orange-500" />
              <span className="text-orange-500 font-bold uppercase tracking-[0.3em] text-xs">Bayiliklerimiz</span>
              <span className="block h-px w-8 bg-orange-500" />
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-bold uppercase leading-none tracking-tight">
              Yetkili <span className="text-orange-500">Bayi</span> &amp; Servis
            </h2>
            <p className="mt-6 max-w-2xl mx-auto text-zinc-300 text-base md:text-lg leading-relaxed">
              Aşağıdaki markaların hem yetkili bayisiyiz hem de orijinal parça garantisiyle servis ve bakım hizmetini sunuyoruz. Motorunuz emin ellerde.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {BRANDS.map((b, i) => (
              <div
                key={b.name}
                data-testid={`brand-${i}`}
                className="group flex flex-col items-center justify-center p-8 md:p-10 bg-black border border-zinc-900 hover:border-orange-600/40 hover:bg-zinc-950 transition-colors"
              >
                <img
                  src={b.logo}
                  alt={b.name}
                  className="h-14 md:h-16 w-auto max-w-full object-contain opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300"
                />
                <span className="mt-5 text-[10px] md:text-xs uppercase tracking-[0.2em] text-zinc-500 group-hover:text-orange-500 transition-colors text-center">
                  Bayi &amp; Servis
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section data-testid="about-section" className="py-24 md:py-32 bg-zinc-950">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="block h-px w-12 bg-orange-500" />
              <span className="text-orange-500 font-bold uppercase tracking-[0.3em] text-xs">Hakkımızda</span>
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-bold uppercase leading-none tracking-tight mb-8">
              Yolun <span className="text-orange-500">Hikayesi</span> Burada Başlar
            </h2>
            <p className="text-zinc-300 text-lg leading-relaxed mb-6">
              Gensan Motors olarak, yılların verdiği tecrübeyle motor tutkunlarına en doğru aracı bulmak için çalışıyoruz. Showroom'umuzda sıfır ve ikinci el motorların yanı sıra ekspertiz, finansman ve servis desteği de sunuyoruz.
            </p>
            <p className="text-zinc-400 leading-relaxed mb-10">
              Müşterilerimize yalnızca bir araç değil, bir yaşam tarzı sunuyoruz. Şehir trafiğinde, uzun yolda ya da pistte — siz seçin, biz hazırlayalım.
            </p>
            <Link to="/hakkimizda" data-testid="about-cta-button" className="inline-flex items-center gap-3 border border-white/40 hover:border-orange-500 hover:bg-orange-600 hover:text-white text-white px-8 py-4 font-bold uppercase tracking-widest text-sm transition-all">
              Daha Fazlasını Keşfet <ArrowRight size={16} />
            </Link>
          </div>
          <div className="relative">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={ABOUT_IMG} alt="Gensan Motors Showroom" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-orange-600 px-6 py-5 hidden md:block">
              <div className="font-display text-4xl font-bold text-white leading-none">{stats.years_experience}+</div>
              <div className="text-xs uppercase tracking-widest text-white mt-1">Yıl Tecrübe</div>
            </div>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      {reviews.length > 0 && (
        <section data-testid="reviews-section" className="py-24 md:py-32 bg-black">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="text-center mb-16">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="block h-px w-8 bg-orange-500" />
                <span className="text-orange-500 font-bold uppercase tracking-[0.3em] text-xs">Yorumlar</span>
                <span className="block h-px w-8 bg-orange-500" />
              </div>
              <h2 className="font-display text-4xl md:text-6xl font-bold uppercase leading-none tracking-tight">
                Müşterilerimiz <span className="text-orange-500">Ne Diyor</span>?
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {reviews.map((r, i) => (
                <div key={r.id || i} data-testid={`review-${i}`} className="bg-zinc-950 border border-zinc-900 p-8 hover:border-orange-600/40 transition-colors">
                  <div className="flex gap-1 mb-6">
                    {Array.from({ length: r.rating || 5 }).map((_, k) => (
                      <Star key={k} size={18} className="fill-orange-500 text-orange-500" />
                    ))}
                  </div>
                  <p className="text-zinc-300 leading-relaxed italic text-lg">"{r.text}"</p>
                  <div className="mt-8 flex items-center gap-4 pt-6 border-t border-zinc-900">
                    <div className="w-12 h-12 bg-orange-600 flex items-center justify-center font-display text-xl font-bold text-white">
                      {r.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-bold uppercase tracking-wide">{r.name}</div>
                      <div className="text-xs uppercase tracking-widest text-zinc-500">Doğrulanmış Müşteri</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Home;
